<template>

      <!-- Header -->
      <div class="page-header">
        <div class="row align-items-center">
          <div class="col-sm-8">
            <h3 class="page-title">
              <i class="fa fa-map-marker-alt mr-2 text-primary"></i>
              Trạng thái xe (GPS)
            </h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">Dashboard</router-link>
              </li>
              <li class="breadcrumb-item active">Theo dõi xe (GPS)</li>
            </ul>
          </div>

          <div class="col-sm-4 text-right">
            <!-- Chọn chế độ lọc -->
            <div class="btn-group btn-group-sm mr-2">
              <!-- <button
                type="button"
                class="btn"
                :class="filterMode === 'ALL' ? 'btn-primary' : 'btn-outline-primary'"
                @click="filterMode = 'ALL'"
              >
                Tất cả xe ({{ gpsItems.length }})
              </button> -->
              <button
                type="button"
                class="btn"
                :class="filterMode === 'ONE' ? 'btn-primary' : 'btn-outline-primary'"
                :disabled="!plate"
                @click="filterMode = 'ONE'"
              >
                Chỉ xe {{ plate || '...' }}
              </button>
            </div>

            <button
              class="btn btn-sm btn-outline-primary"
              @click="fetchGps"
              :disabled="isLoading"
              title="Tải lại"
            >
              <i class="fa fa-sync" :class="{ 'fa-spin': isLoading }"></i>
              <span class="ml-1">Tải lại</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="row">
        <div class="col-lg-8 col-md-12">
          <div class="card shadow-sm">
            <div class="card-header d-flex justify-content-between align-items-center">
              <span class="font-weight-bold">
                <i class="fa fa-truck mr-2 text-primary"></i>Xe GPS
                <span v-if="filterMode === 'ONE' && plate">
                  – lọc theo: {{ plate }}
                </span>
              </span>
              <small v-if="lastUpdated" class="text-muted">
                Cập nhật lúc: {{ lastUpdated }}
              </small>
            </div>

            <div class="card-body">
              <!-- Loading -->
              <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 mb-0 text-muted">Đang lấy dữ liệu GPS...</p>
              </div>

              <!-- Error -->
              <div v-else-if="error" class="alert alert-danger mb-0">
                <i class="fa fa-exclamation-triangle mr-2"></i>
                {{ error }}
              </div>

              <!-- Không có dữ liệu -->
              <div v-else-if="visibleGpsItems.length === 0" class="text-center py-4 text-muted">
                <span v-if="filterMode === 'ONE' && plate">
                  Không tìm thấy dữ liệu GPS cho biển số <strong>{{ plate }}</strong>.
                </span>
                <span v-else>
                  Không có dữ liệu GPS trả về.
                </span>
              </div>

              <!-- Danh sách xe -->
              <div v-else>
                <div
                  v-for="item in visibleGpsItems"
                  :key="item.Id"
                  class="gps-vehicle-item mb-3 p-3 border rounded"
                >
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h5 class="mb-0 font-weight-bold">
                        <i class="fa fa-truck mr-2 text-primary"></i>
                        {{ item.Plate || "Không rõ biển số" }}
                      </h5>
                      <small class="text-muted">
                        Thiết bị: {{ item.Id }} · Nhóm: {{ item.GroupName }}
                      </small>
                    </div>
                    <div class="text-right">
                      <span
                        class="badge"
                        :class="statusBadgeClass(itemStatus(item))"
                      >
                        {{ itemStatus(item).label }}
                      </span>
                      <div class="small text-muted mt-1">
                        Tốc độ: {{ formatSpeed(item.Speed) }} km/h
                      </div>
                      <div class="small text-muted mt-1">
                        Số km đã đi (trong ngày): {{ formatNumber(item.Km) }} km
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-7">
                      <p class="mb-1">
                        <i class="fa fa-map-marker-alt mr-1 text-danger"></i>
                        <strong>Vị trí:</strong>
                        <span>{{ item.Address || "Không rõ địa chỉ" }}</span>
                      </p>
                      <p class="mb-1">
                        <i class="fa fa-clock mr-1 text-info"></i>
                        <strong>Thời gian cập nhật:</strong>
                        <span>{{ item.TimeUpdate }}</span>
                      </p>
                      <p class="mb-0" v-if="item.StopTime">
                        <i class="fa fa-stop-circle mr-1 text-secondary"></i>
                        <strong>Thời gian dừng:</strong>
                        <span>{{ item.StopTime }} (lần {{ item.StopCounter }})</span>
                      </p>
                      <p class="mb-0" v-if="item.BeginStop">
                        <i class="fa fa-stop-circle mr-1 text-secondary"></i>
                        <strong>Bắt đầu dừng:</strong>
                        <span>{{ formatDateTime(item.BeginStop) }}</span>
                      </p>
                    </div>

                    <div class="col-md-5">
                      <ul class="list-unstyled mb-0 small">
                        <li>
                          <i
                            class="fa mr-1"
                            :class="item.Gps ? 'fa-satellite-dish text-success' : 'fa-satellite-dish text-muted'"
                          ></i>
                          GPS:
                          <strong :class="item.Gps ? 'text-success' : 'text-muted'">
                            {{ item.Gps ? "Có tín hiệu" : "Mất / yếu" }}
                          </strong>
                        </li>
                        <li>
                          <i
                            class="fa mr-1"
                            :class="item.Gsm ? 'fa-signal text-success' : 'fa-signal text-muted'"
                          ></i>
                          GSM:
                          <strong :class="item.Gsm ? 'text-success' : 'text-muted'">
                            {{ item.Gsm ? "Có sóng" : "Yếu / mất sóng" }}
                          </strong>
                        </li>
                        <li>
                          <i
                            class="fa mr-1"
                            :class="item.Key ? 'fa-key text-success' : 'fa-key text-muted'"
                          ></i>
                          Khóa:
                          <strong>{{ item.Key ? "Bật" : "Tắt" }}</strong>
                        </li>
                        <li>
                          <i
                            class="fa mr-1"
                            :class="item.Door ? 'fa-door-open text-success' : 'fa-door-closed text-muted'"
                          ></i>
                          Cửa:
                          <strong>{{ item.Door ? "Mở" : "Đóng" }}</strong>
                        </li>
                        <li v-if="item.DayDrivingTime != null">
                          <i class="fa fa-road mr-1 text-primary"></i>
                          Lái trong ngày: <strong>{{ item.DayDrivingTime }} phút</strong>
                        </li>
                        <li v-if="item.Over4h || item.Over10h">
                          <i class="fa fa-exclamation-circle mr-1 text-danger"></i>
                          <span class="text-danger">
                            {{ item.Over4h ? "Vượt 4h liên tục. " : "" }}
                            {{ item.Over10h ? "Vượt 10h/ngày." : "" }}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel mô tả trạng thái -->
        <div class="col-lg-4 col-md-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <strong><i class="fa fa-info-circle mr-2"></i>Ý nghĩa trạng thái</strong>
            </div>
            <div class="card-body small">
              <p class="mb-1">
                <span class="badge badge-success mr-1">Đang chạy</span>
                Xe đang di chuyển (Speed &gt; 2 km/h, IsStop = false).
              </p>
              <p class="mb-1">
                <span class="badge badge-warning mr-1">Đang dừng</span>
                Xe dừng (IsStop = true), nhưng vẫn có tín hiệu GPS.
              </p>
              <p class="mb-1">
                <span class="badge badge-secondary mr-1">Tắt máy / đứng lâu</span>
                Dừng + StopTime dài (nghỉ, chờ việc...).
              </p>
              <p class="mb-0">
                <span class="badge badge-danger mr-1">Mất tín hiệu</span>
                Không có GPS hoặc quá lâu không cập nhật.
              </p>
            </div>
          </div>
        </div>
      </div>

</template>

<script>
import { GpsService } from "@/services/gpsService";
import { formatDateTime } from "@/helpers/datetime";

export default {
  name: "XeGpsTrangThai",

  props: {
    // Biển số từ hệ thống CRM (nếu truyền vào)
    plate: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      isLoading: false,
      error: null,
      gpsItems: [], // Data từ API (4 xe)
      lastUpdated: null,
      filterMode: "ALL", // "ALL" | "ONE"
    };
  },

  computed: {
    normalizedPlate() {
      if (!this.plate) return null;
      return this.normalizePlate(this.plate);
    },

    visibleGpsItems() {
      // chỉ lấy xe có Plate
      const withPlate = this.gpsItems.filter(
        (x) => x.Plate && x.Plate.toString().trim().length > 0
      );

      // hiển thị tất cả
      // if (this.filterMode === "ALL" || !this.normalizedPlate) {
      //   return withPlate;
      // }

      // chỉ xe có biển số trùng plate
      return withPlate.filter(
        (x) => this.normalizePlate(x.Plate) === this.normalizedPlate
      );
    },
  },

  methods: {
    formatDateTime,

    normalizePlate(plate) {
      if (!plate) return "";
      return plate.toString().toUpperCase().replace(/[\s-]/g, "");
    },

    async fetchGps() {
      this.isLoading = true;
      this.error = null;
      try {
        const data = await GpsService.getMyVehicleStatus();
        const rawItems = Array.isArray(data?.Data) ? data.Data : [];
        this.gpsItems = rawItems;
        this.lastUpdated = new Date().toLocaleString();
      } catch (e) {
        console.error("Lỗi gọi GPS API:", e);
        this.error =
          "Không lấy được dữ liệu GPS. Có thể do CORS hoặc tài khoản / mật khẩu sai.";
      } finally {
        this.isLoading = false;
      }
    },

    formatSpeed(speed) {
      if (speed == null || isNaN(speed)) return 0;
      return Number(speed).toFixed(1);
    },

    formatNumber(value) {
      if (value == null || isNaN(value)) return 0;
      return Number(value).toFixed(1);
    },

    itemStatus(item) {
      if (!item.Gps) {
        return { code: "NO_SIGNAL", label: "Mất tín hiệu" };
      }
      const speed = Number(item.Speed || 0);
      const isStop = !!item.IsStop;

      if (!isStop && speed > 2) {
        return { code: "RUNNING", label: "Đang chạy" };
      }
      if (isStop && item.StopTime) {
        return { code: "STOP_LONG", label: "Đang dừng (lâu)" };
      }
      return { code: "STOP", label: "Đang dừng / tắt máy" };
    },

    statusBadgeClass(status) {
      switch (status.code) {
        case "RUNNING":
          return "badge-success";
        case "STOP":
          return "badge-warning";
        case "STOP_LONG":
          return "badge-secondary";
        case "NO_SIGNAL":
          return "badge-danger";
        default:
          return "badge-light";
      }
    },
  },

  created() {
    this.fetchGps();
  },

  watch: {
    // Nếu chọn xe khác ở ngoài, và đang ở chế độ ONE thì vẫn dùng plate mới để lọc
    plate(newVal, oldVal) {
      if (newVal && this.filterMode === "ONE") {
        // chỉ cần đổi lọc, không cần fetch lại (nếu muốn có thể fetch)
        // this.fetchGps();
      }
    },
  },
};
</script>

<style scoped>
.gps-vehicle-item {
  background-color: #fdfdfd;
}

@media (max-width: 767.98px) {
  .gps-vehicle-item {
    padding: 1rem 0.75rem;
  }
}
</style>
